<script setup lang="ts">
import { computed } from 'vue'

import BaseCard, { type BaseCardProps } from '@/components/BaseCard.vue'

export type RichCardProps = BaseCardProps & {
  /** Card title */
  title?: string
  /** Card description (supports HTML) */
  description?: string
  /** URL for the card image */
  imageUrl?: string
  /** Alt text for the card image. If not provided or matches title, treated as decorative. */
  imageAlt?: string
  /** HTML heading level for the title */
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const props = withDefaults(defineProps<RichCardProps>(), {
  headingLevel: 'h3',
})

// Image is decorative if alt text matches title (would be redundant) or if no alt text is provided. Returns empty string for decorative images so screen readers skip them (missing alt would announce the URL).
const computedImageAlt = computed(() => {
  if (props.imageAlt?.toLowerCase() === props.title?.toLowerCase()) return ''

  return props.imageAlt ?? ''
})
</script>

<template>
  <BaseCard
    :is="is"
    :class="[props.class, 'rich-card', { 'with-image': imageUrl }]"
    :hoverable="hoverable"
    :unpadded="props.unpadded || Boolean(imageUrl)"
  >
    <slot name="overlay-slot" />

    <div class="content">
      <slot name="image-slot">
        <div v-if="imageUrl" class="image">
          <img :alt="computedImageAlt" loading="lazy" :src="imageUrl" />
        </div>
      </slot>

      <div class="body">
        <slot name="header-slot">
          <component :is="headingLevel" v-if="title" class="title">
            {{ title }}
          </component>
        </slot>

        <slot name="description-slot">
          <p v-if="description" v-shtml="description" class="description" />
        </slot>
      </div>
    </div>
  </BaseCard>
</template>

<style scoped>
.rich-card {
  /* Local Themable Variables */
  --_border-width: var(--rich-card-border-width, var(--base-card-border-width, var(--border-width)));
  --_border-radius: var(--rich-card-border-radius, var(--base-card-border-radius, var(--card-border-radius)));
  --_transition-duration: var(
    --rich-card-transition-duration,
    var(--base-card-transition-duration, var(--transition-duration))
  );
  --_font-size-md: var(--rich-card-font-size-md, var(--size-md));
  --_font-size-sm: var(--rich-card-font-size-sm, var(--size-sm));
  --_image-aspect-ratio: var(--rich-card-image-aspect-ratio, var(--image-aspect-ratio, 16 / 9));

  /* Reset styles for interactive elements (button, a) */
  font: inherit;
  color: inherit;
  text-decoration: none;
  text-align: inherit;

  &.hoverable:hover {
    .image img {
      transform: scale(var(--image-transform-scale));
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    height: 100%;

    .image {
      width: 100%;
      aspect-ratio: var(--_image-aspect-ratio);
      overflow: hidden;
      /* Subtract the border width from the card's border radius to avoid overlap */
      border-top-left-radius: calc(var(--_border-radius) - var(--_border-width));
      border-top-right-radius: calc(var(--_border-radius) - var(--_border-width));

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        transition: transform var(--_transition-duration) ease-out;
      }
    }

    .body {
      display: flex;
      flex-direction: column;
      gap: var(--space-0-5);
      padding: var(--space-1-5);

      .title {
        font-weight: var(--font-weight-semibold);
        font-size: var(--_font-size-md);
        margin: 0;
      }

      .description {
        font-size: var(--_font-size-sm);
        color: var(--text-secondary);
        line-height: var(--line-height-body);
        margin: 0;
      }
    }

    &:not(:has(.image)) {
      .body {
        padding: 0;
      }
    }
  }
}
</style>
